import { useEffect, useState } from "react";

import type { SortingState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import type { DataTableProps } from "@/shared/types";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { ActiveFilters } from "@/features/ActiveFilters";
import { Label } from "../label";
import { Skeleton } from "../skeleton";
import { MobileDataTable } from "./MobileDataTable";

export function DataTable<TData, TValue>({
  columns,
  data,
  pageCount,
  page,
  setPage,
  isLoading,
  selectedState,
  setSelectedState,
  setAllSelectedState,
  getRowId,
  selectMobileColumn,
  found,
  refetch,
  noPagination,
  limitedControl,
  lastParamsPagination,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [lastParamsPaginationState, setLastParamsPaginationState] = useState<{
    history: Array<{
      lastCreatedAt: string | null;
      lastId: string | null;
    }>;
    page: number;
    resetKey: string;
  }>({
    history: [{ lastCreatedAt: null, lastId: null }],
    page: 1,
    resetKey: "",
  });

  const isMobile = useIsMobile();
  const isLastParamsPagination = !!lastParamsPagination;
  const currentLastParamsPaginationState =
    isLastParamsPagination &&
    lastParamsPaginationState.resetKey === lastParamsPagination.resetKey
      ? lastParamsPaginationState
      : {
          history: [{ lastCreatedAt: null, lastId: null }],
          page: 1,
          resetKey: lastParamsPagination?.resetKey ?? "",
        };

  const currentPage = isLastParamsPagination
    ? currentLastParamsPaginationState.page
    : page;
  const canGoPrevious = isLastParamsPagination
    ? currentLastParamsPaginationState.page > 1
    : !!page && page > 1;
  const canGoNext = isLastParamsPagination
    ? !!data?.length &&
      currentLastParamsPaginationState.page * lastParamsPagination.pageSize <
        lastParamsPagination.total
    : !!page && !!pageCount && page < pageCount;

  const handlePreviousPage = () => {
    if (isLastParamsPagination) {
      if (currentLastParamsPaginationState.history.length === 1) {
        return;
      }

      const nextHistory = currentLastParamsPaginationState.history.slice(0, -1);
      const previousLastParams = nextHistory[nextHistory.length - 1] ?? {
        lastCreatedAt: null,
        lastId: null,
      };

      setLastParamsPaginationState({
        history: nextHistory,
        page: Math.max(1, currentLastParamsPaginationState.page - 1),
        resetKey: lastParamsPagination.resetKey,
      });
      lastParamsPagination.setLastParams(previousLastParams);

      return;
    }

    if (setPage && page) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (isLastParamsPagination) {
      const lastRow = data?.[data.length - 1];
      const nextLastParams =
        lastRow && lastParamsPagination.getLastParams(lastRow);

      if (!nextLastParams?.lastId || !nextLastParams.lastCreatedAt) {
        return;
      }

      setLastParamsPaginationState({
        history: [...currentLastParamsPaginationState.history, nextLastParams],
        page: currentLastParamsPaginationState.page + 1,
        resetKey: lastParamsPagination.resetKey,
      });
      lastParamsPagination.setLastParams({
        lastCreatedAt: nextLastParams.lastCreatedAt,
        lastId: nextLastParams.lastId,
      });

      return;
    }

    if (setPage && page) {
      setPage(page + 1);
    }
  };

  const handleLastPage = () => {
    if (setPage) {
      setPage(pageCount || 0);
    }
  };

  const handleFirstPage = () => {
    if (isLastParamsPagination) {
      setLastParamsPaginationState({
        history: [{ lastCreatedAt: null, lastId: null }],
        page: 1,
        resetKey: lastParamsPagination.resetKey,
      });
      lastParamsPagination.setLastParams({
        lastCreatedAt: null,
        lastId: null,
      });

      return;
    }

    if (setPage) {
      setPage(1);
    }
  };

  const table = useReactTable({
    data: data || [],
    pageCount,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    enableRowSelection: true,
    getSortedRowModel: getSortedRowModel(),
    getRowId,
    state: { sorting, rowSelection },
    onRowSelectionChange: setRowSelection,
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    debugTable: true,
  });

  useEffect(() => {
    if (!isLastParamsPagination) {
      return;
    }

    setLastParamsPaginationState((prevState) =>
      prevState.resetKey === lastParamsPagination.resetKey
        ? prevState
        : {
            history: [{ lastCreatedAt: null, lastId: null }],
            page: 1,
            resetKey: lastParamsPagination.resetKey,
          },
    );
  }, [isLastParamsPagination, lastParamsPagination?.resetKey]);

  useEffect(() => {
    if (setSelectedState) {
      const rows = table.getSelectedRowModel().rows;
      setSelectedState(rows.map((row) => row.original));
    }
  }, [rowSelection]);

  useEffect(() => {
    if (selectedState?.length === 0) {
      const rows = table.getSelectedRowModel().rows;

      if (rows.length > 0 && selectedState?.length === 0) {
        setRowSelection({});
      }
      if (table.getIsAllPageRowsSelected()) {
        setAllSelectedState?.(true);
      }
    }
  }, [selectedState]);

  if (isMobile)
    return (
      <MobileDataTable
        data={data}
        isLoading={isLoading}
        table={table}
        selectMobileColumn={selectMobileColumn}
        pagination={{
          canGoNext,
          canGoPrevious,
          isLastIdPagination: isLastParamsPagination,
          onNextPage: handleNextPage,
          onPreviousPage: handlePreviousPage,
          page: currentPage,
          pageCount,
        }}
        refetch={refetch}
      />
    );

  return (
    <div className="relative mt-5">
      {refetch && <ActiveFilters refetch={refetch} />}
      <div className="rounded-md border border-strokeColor">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className={cn("px-4")}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array.from({ length: 10 }, (_, index) => index)].map(
                (index) => (
                  <TableRow key={index}>
                    <TableCell className="pl-4 pr-0">
                      <Skeleton className="h-10 w-full rounded-r-none" />
                    </TableCell>
                    {table.getHeaderGroups()[0].headers.map((_, cellIndex) => (
                      <TableCell className="px-0" key={cellIndex}>
                        <Skeleton className="h-10 w-full rounded-none" />
                      </TableCell>
                    ))}
                    <TableCell className="pl-0 pr-4">
                      <Skeleton className="h-10 w-full rounded-l-none" />
                    </TableCell>
                  </TableRow>
                ),
              )
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell className="px-4 text-textColor" key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-textColor"
                >
                  Ничего нет.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!noPagination && (
        <div className="flex items-center justify-between py-4">
          <div className="flex space-x-2">
            {!limitedControl && !isLastParamsPagination && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canGoPrevious || isLoading}
                  onClick={handleFirstPage}
                  className="text-textColor"
                >
                  На первую страницу
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLastPage}
                  className="text-textColor"
                  disabled={
                    !pageCount ||
                    !currentPage ||
                    currentPage === pageCount ||
                    isLoading
                  }
                >
                  На последнюю страницу
                </Button>
              </>
            )}
            {!limitedControl && isLastParamsPagination && (
              <Button
                variant="outline"
                size="sm"
                disabled={!canGoPrevious || isLoading}
                onClick={handleFirstPage}
                className="text-textColor"
              >
                На первую страницу
              </Button>
            )}
          </div>
          {found && <Label>Найдено: {found}</Label>}
          <div className="flex gap-2">
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!canGoPrevious || isLoading}
                onClick={handlePreviousPage}
                className="text-textColor"
              >
                Назад
              </Button>
              {currentPage ? (
                <div className="text-sm text-textColor">
                  {currentPage}
                  {!limitedControl && !isLastParamsPagination && pageCount
                    ? ` из ${pageCount}`
                    : ""}
                </div>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                className="text-textColor"
                disabled={!canGoNext || isLoading}
              >
                Вперед
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
